import { ResearchCategory, ReportSummary } from '@snapi/types';
import {
  ResearchCategoryToBackgroundColor,
  ResearchCategoryToColor,
} from '@snapi/ui-utils';
import { useReportContext } from '../../report-context';
import { useMemo } from 'react';
import { DashboardCard } from '../dashboard-card';
import { ReportUtils } from '@snapi/utils';
import { PieChart } from 'react-minimal-pie-chart';
import { DevInfo, Percentage } from '@snapi/luxury';
import clsx from 'clsx';
import {NewDashboardCard} from '../new-dashboard-card'

interface CategorySummary {
  category: ResearchCategory;
  color: string;
  value: number;
}

interface CategorySummaryPieProps {
  data: CategorySummary[];
}

export const CategorySummaryPie: React.FC<CategorySummaryPieProps> = ({
  data,
}: CategorySummaryPieProps) => {
  const dominantCategory = useMemo(
    () => data.sort((a, b) => b.value - a.value)[0],
    [data]
  );

  return (
    <div className="flex relative max-w-[256px] md:max-w-[336px] lg:max-w-[256px]">
      <div className="absolute bottom-5 lg:bottom-2 xl:bottom-4 right-0 left-0 flex items-end justify-center">
        <div className="text-center">
          <h4 className="uppercase text-[16px]">
            {dominantCategory.value.toFixed(1)} %
            <br />
            {dominantCategory.category}
          </h4>
        </div>
      </div>
      <PieChart
        data={data}
        startAngle={190}
        lengthAngle={160}
        viewBoxSize={[100, 45]}
        lineWidth={10}
        rounded
        paddingAngle={8}
        totalValue={100}
      />
      <div className="absolute top-0 left-0 right-0 bottom-0 mx-[22px] mt-2">
        <PieChart
          data={new Array(25)
            .fill(null)
            .map(() => ({ value: 4, color: 'rgba(0, 0, 0, .3)' }))}
          startAngle={180}
          lengthAngle={180}
          viewBoxSize={[100, 40]}
          lineWidth={0.5}
          paddingAngle={2}
          totalValue={100}
        />
      </div>
    </div>
  );
};

const GutBalanceInfo = [
  {
    title: 'What is gut balance?',
    description:
      'Gut balance focuses on the beneficial vs disruptive bacteria. The balance of these two types of bacteria is crucial for a healthy gut.',
  },
  {
    title: 'Why is gut balance important?',
    description:
      'Having both protective and disruptive bacteria is super important! Disruptive or “bad” bacteria can exist at low levels in the body without causing harm while also creating a tolerance to certain bacteria and viruses.',
  },
  {
    title: 'What does my result mean?',
    description:
      'If you see a red tab, don’t be alarmed! Your balance may be considered lower than average- but we’re here to improve that. If you see a green tab, that means things are looking good and balance is on track!',
  },
];

export const GutBalanceCard: React.FC<
  Pick<ReportSummary['balance'], 'protective' | 'disruptive' | 'neutral'>
> = ({ protective, disruptive, neutral }) => {
  const { baby } = useReportContext();

  const summary = useMemo<CategorySummary[]>(
    () => [
      {
        category: ResearchCategory.protective,
        color: ResearchCategoryToColor[ResearchCategory.protective],
        value: protective,
      },
      {
        category: ResearchCategory.disruptive,
        color: ResearchCategoryToColor[ResearchCategory.disruptive],
        value: disruptive,
      },
      {
        category: ResearchCategory.neutral,
        color: ResearchCategoryToColor[ResearchCategory.neutral],
        value: neutral,
      },
      {
        category: ResearchCategory.other,
        color: ResearchCategoryToColor[ResearchCategory.other],
        value: 100 - protective - disruptive - neutral,
      },
    ],
    [protective, disruptive, neutral]
  );

  const { good, score } = useMemo(() => {
    const score = ReportUtils.balance.calculateScore(protective, disruptive);
    const good = ReportUtils.balance.isGood(score);
    return { good, score };
  }, [protective, disruptive]);

  return (
    <NewDashboardCard
      badge={{ good, status: good ? 'Nice work' : 'Attention Needed' }}
      title="Gut Balance"
      summary={`${baby.name}’s gut balance is dominated by ${
        good
          ? 'protective bacteria. Nice work!'
          : 'disruptive bacteria. Let’s work on it!'
      }`}
      info={GutBalanceInfo}
    >
      <DevInfo>
        <span className="absolute top-[10px] left-[120px] font-mono font-bold">
          {score}
        </span>
      </DevInfo>
      <div className="flex flex-1 flex-col justify-center">
        <div className="flex justify-center lg:pt-8">
          <CategorySummaryPie data={summary} />
        </div>
        <div className="grid grid-cols-2 gap-3 mt-5 md:gap-x-7 lg:gap-x-1 xl:gap-x-7">
          {summary.map(({ category, value }) => (
            <div className="flex flex-1">
              <span
                className={clsx(
                  'self-center justify-center w-3 h-3 rounded-full',
                  ResearchCategoryToBackgroundColor[category]
                )}
              />
              <small className="pl-2 lg:pl-1 xl:pl-2">
                <Percentage percentage={value} /> - {category}
              </small>
            </div>
          ))}
        </div>
      </div>
    </NewDashboardCard>
  );
};
